import React, { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Wrench, Lightning, BatteryFull as Battery, GearSix, SealCheck as CircleWavyCheck } from "@phosphor-icons/react";
import useSeo from "@/hooks/useSeo";

// Icon key → Phosphor component. Admin picks the key from a dropdown
// when creating/editing the service in /admin/services.
const ICON_MAP = {
  wrench: Wrench,
  lightning: Lightning,
  gear: GearSix,
  battery: Battery,
  wheel: CircleWavyCheck,
  check: CircleWavyCheck,
};

const HERO = "https://images.unsplash.com/photo-1675798227643-da319f8ee8f7?w=1600&q=85";

export default function ServicePage() {
  useSeo({
    title: "Bicycle Service & Workshop",
    description: "Bosch certified e-bike diagnostics, motor repair, suspension service, wheel building and more — professional bicycle workshop for the Balkans.",
    path: "/service",
  });
  const { t } = useLang();
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ preferred_date: "", customer_name: "", customer_email: "", customer_phone: "", bike_details: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Public services endpoint returns active-only, sorted by admin order.
    api.get("/services").then((r) => {
      setServices(r.data);
      if (r.data[0]) setSelected(r.data[0].key);
    }).finally(() => setLoadingServices(false));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Pick a service first"); return; }
    setSubmitting(true);
    try {
      await api.post("/service/bookings", { ...form, service_type: selected });
      toast.success("Booking received. We'll call you soon.");
      setForm({ preferred_date: "", customer_name: "", customer_email: "", customer_phone: "", bike_details: "", notes: "" });
    } catch { toast.error("Please try again."); }
    finally { setSubmitting(false); }
  };

  const selectedService = services.find((s) => s.key === selected);
  const priceLabel = (svc) => `${svc.price_from ? "from " : ""}€${Number(svc.price || 0).toFixed(0)}`;

  return (
    <div data-testid="service-page">
      <section className="relative h-[60vh] overflow-hidden">
        <img src={HERO} alt="Bike service" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex items-end pb-12">
          <div>
            <div className="overline text-[color:var(--vt-orange)] mb-3">/ WORKSHOP</div>
            <h1 className="font-display font-black text-white text-6xl md:text-8xl tracking-tighter">{t.service.title}</h1>
            <p className="text-neutral-200 text-lg mt-3">{t.service.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <h2 className="font-display font-black text-4xl tracking-tighter mb-8">Services</h2>

        {loadingServices ? (
          <div className="p-12 text-center text-neutral-500">Loading services…</div>
        ) : services.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 border border-neutral-200">
            No services published yet — please check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-16" data-testid="services-grid">
            {services.map((s) => {
              const Icon = ICON_MAP[s.icon] || Wrench;
              return (
                <button
                  key={s.key}
                  onClick={() => setSelected(s.key)}
                  className={`border p-6 text-left transition-colors ${selected === s.key ? "border-[color:var(--vt-orange)] bg-[color:var(--vt-orange)]/5" : "border-neutral-200 hover:border-black"}`}
                  data-testid={`service-${s.key}`}
                >
                  {s.image ? (
                    <img src={s.image} alt={s.name} className="w-10 h-10 object-cover mb-3 border border-neutral-200" />
                  ) : (
                    <Icon size={28} weight="duotone" className="mb-3 text-[color:var(--vt-orange)]" />
                  )}
                  <div className="font-bold text-sm mb-1">{s.name}</div>
                  <div className="text-xs text-neutral-500">{priceLabel(s)}</div>
                  {s.duration && <div className="text-[10px] text-neutral-400 mt-1">{s.duration}</div>}
                </button>
              );
            })}
          </div>
        )}

        {selectedService && selectedService.description && (
          <div className="max-w-2xl mx-auto mb-8 p-6 border border-neutral-200 bg-neutral-50" data-testid="service-detail">
            <div className="overline text-[color:var(--vt-orange)] mb-1">{selectedService.category}</div>
            <h3 className="font-display font-bold text-xl mb-2">{selectedService.name}</h3>
            <p className="text-sm text-neutral-700">{selectedService.description}</p>
          </div>
        )}

        {services.length > 0 && (
          <div className="border border-neutral-200 p-8 max-w-2xl mx-auto">
            <h3 className="font-display font-bold text-2xl mb-6">{t.service.bookService}</h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="text-sm">
                <div className="overline text-neutral-600 mb-2">{t.service.serviceType}</div>
                <div className="font-bold">{selectedService?.name || "—"}</div>
              </div>
              <input required type="date" className="vt-input" value={form.preferred_date} onChange={upd("preferred_date")} data-testid="service-date" />
              <input required className="vt-input" placeholder="Full name" value={form.customer_name} onChange={upd("customer_name")} data-testid="service-name" />
              <input required type="email" className="vt-input" placeholder="Email" value={form.customer_email} onChange={upd("customer_email")} data-testid="service-email" />
              <input required className="vt-input" placeholder="Phone" value={form.customer_phone} onChange={upd("customer_phone")} data-testid="service-phone" />
              <input className="vt-input" placeholder={t.service.bikeDetails} value={form.bike_details} onChange={upd("bike_details")} />
              <textarea className="vt-input" rows="3" placeholder="Notes" value={form.notes} onChange={upd("notes")} />
              <button disabled={submitting} className="vt-btn-orange w-full" data-testid="service-submit">{submitting ? "..." : t.service.bookService}</button>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
